import { useEffect, useState } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { ClipboardCheck, AlertCircle } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

interface StockCheckProtectedRouteProps {
  children: React.ReactNode;
}

export const StockCheckProtectedRoute = ({ children }: StockCheckProtectedRouteProps) => {
  const { user, company, loading: authLoading } = useAuth();
  const { token } = useParams<{ token: string }>();
  const location = useLocation();
  const [showContent, setShowContent] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  const [accessError, setAccessError] = useState<string | null>(null);

  useEffect(() => {
    const validateAccess = async () => {
      // Wait for auth to load
      if (authLoading) return;

      // No user, will redirect to login
      if (!user) {
        setIsValidating(false);
        return;
      }

      // Wait for company to load
      if (!company) return;

      // Validate that user belongs to the checklist's company
      if (token) {
        try {
          const { data: checklist, error } = await supabase
            .from('stock_checklists')
            .select('company_id')
            .eq('access_token', token)
            .single();

          if (error || !checklist) {
            setAccessError('Conferência não encontrada ou link inválido.');
            setIsValidating(false);
            return;
          }

          if (checklist.company_id !== company.id) {
            setAccessError('Você não tem permissão para acessar esta conferência. Ela pertence a outra empresa.');
            setIsValidating(false);
            return;
          }

          // All checks passed
          setShowContent(true);
          setIsValidating(false);
        } catch (err) {
          console.error('Erro ao validar acesso:', err);
          setAccessError('Erro ao validar acesso à conferência.');
          setIsValidating(false);
        }
      }
    };

    validateAccess();
  }, [authLoading, user, company, token]);

  // Show loading state (includes waiting for permissions when content is ready)
  if (authLoading || isValidating || (user && !company)) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-emerald-950 to-slate-900 flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <div className="bg-emerald-500/20 p-4 rounded-xl">
            <ClipboardCheck className="h-12 w-12 text-emerald-500 animate-pulse" />
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-emerald-500 rounded-full animate-pulse"></div>
            <div className="w-3 h-3 bg-emerald-500 rounded-full animate-pulse" style={{ animationDelay: '150ms' }}></div>
            <div className="w-3 h-3 bg-emerald-500 rounded-full animate-pulse" style={{ animationDelay: '300ms' }}></div>
          </div>
          <p className="text-emerald-200/70 text-sm">Verificando acesso...</p>
        </div>
      </div>
    );
  }

  // Not authenticated - redirect to stock check auth with token
  if (!user) {
    return <Navigate to={`/conferencia-auth?token=${token}`} state={{ from: location }} replace />;
  }

  // Access error - show error page
  if (accessError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-emerald-950 to-slate-900 flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-slate-800/90 border-red-500/30">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-white mb-2">Acesso Negado</h2>
            <p className="text-slate-300 mb-6">{accessError}</p>
            <Button
              variant="outline"
              className="border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10"
              onClick={() => window.close()}
            >
              Fechar
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // A7b (I.160): a chave (ou a senha da conferência) é decidida pelo banco dentro da página
  // (get_stock_checklist_access); aqui ficam só login e empresa.

  // Valid access - render children
  return showContent ? <>{children}</> : null;
};
